import PageLayout from "@/components/feature/PageLayout";
import ScrollReveal from "@/components/base/ScrollReveal";

const issues = [
  "Anxiety and panic",
  "Depression and low mood",
  "Burnout and stress",
  "Relationship challenges",
  "Life transitions",
  "Identity and self-worth",
  "Grief and loss",
  "Perfectionism and self-criticism",
];

const whatToExpect = [
  {
    num: "01",
    title: "Understanding, not just coping",
    body: "We help you get clear on why you're feeling what you're feeling. Not just managing symptoms, but understanding the root causes so you can actually change the patterns that aren't working.",
    color: "#B4A6E3",
  },
  {
    num: "02",
    title: "A collaborative relationship",
    body: "Therapy works best when it feels like a genuine partnership. We listen carefully, ask the right questions, and work with you to build a plan that makes sense for your life and goals.",
    color: "#7D54FC",
  },
  {
    num: "03",
    title: "Evidence-based, personalized methods",
    body: "Our therapists draw from CBT, DBT, psychodynamic therapy, ACT, and mindfulness-based approaches. We tailor the method to you, not the other way around.",
    color: "#3C6FF8",
  },
  {
    num: "04",
    title: "Flexible scheduling and format",
    body: "Telehealth from anywhere in NY, NJ, or Indiana. In-person sessions available in Larchmont. Evening and weekend appointments to fit your schedule.",
    color: "#6B742F",
  },
];

export default function IndividualTherapy() {
  return (
    <>
      <PageLayout>
        {/* Hero */}
        <section className="relative w-full min-h-[520px] md:min-h-[620px] flex items-center overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src="/images/Individual/Individual-hero.png"
              alt="Individual therapy at Perspective"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0" style={{ background: "linear-gradient(120deg, rgba(28,20,12,0.48) 0%, rgba(42,30,18,0.32) 50%, rgba(60,40,20,0.14) 100%)" }} />
            <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(20,14,8,0.38) 0%, transparent 50%)" }} />
          </div>
          <div className="relative z-10 w-full px-6 md:px-12 lg:px-20 pt-36 pb-20">
            <div className="max-w-2xl">
              <p className="font-manrope text-white/90 text-[13px] font-semibold tracking-widest uppercase mb-4">
                Individual Therapy
              </p>
              <h1 className="font-fraunces text-white text-4xl md:text-5xl lg:text-[3.4rem] font-light leading-[1.15] mb-6">
                Navigate what life is{" "}
                <em className="not-italic font-normal" style={{ color: "#C9BCEE" }}>
                  actually asking of you
                </em>
              </h1>
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-3 md:p-4 max-w-lg">
                <p className="font-manrope text-[#2B2B2B]/90 text-lg leading-relaxed">
                  One-on-one therapy for adults at every stage. We help you understand the patterns driving your struggles and build a clear path forward.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* What we help with */}
        <section className="w-full py-20 md:py-28">
          <div className="w-full px-6 md:px-12 lg:px-20">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
              <ScrollReveal variant="fadeLeft" delay={0}>
                <div className="w-full h-[280px] sm:h-[340px] md:h-[500px] rounded-2xl overflow-hidden">
                  <img
                    src="/images/Individual/Individual-supporting.png"
                    alt="Individual therapy session"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </ScrollReveal>
              <div>
                <ScrollReveal variant="fadeIn" delay={0}>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-5 h-[2px] rounded-full" style={{ background: "linear-gradient(90deg, #3C6FF8, #7D54FC)" }} />
                    <span className="font-manrope text-[13px] font-semibold tracking-widest uppercase" style={{ color: "#3C6FF8" }}>
                      What We Help With
                    </span>
                  </div>
                </ScrollReveal>
                <ScrollReveal variant="fadeUp" delay={80}>
                  <h2 className="font-fraunces text-[#2B2B2B] text-3xl md:text-4xl font-light leading-snug mb-6">
                    Whatever you're dealing with,{" "}
                    <em className="not-italic" style={{ color: "#7D54FC" }}>we can work through it together</em>
                  </h2>
                </ScrollReveal>
                <ScrollReveal variant="fadeUp" delay={160}>
                  <p className="font-manrope text-[#2B2B2B]/82 text-[15px] leading-relaxed mb-8">
                    Anxiety, burnout, relationship struggles, life transitions, identity questions, grief. These aren't signs of weakness. They're signals that something needs attention. Our individual therapy helps you understand what's really going on beneath the surface, and gives you the tools to move through it with clarity and confidence.
                  </p>
                </ScrollReveal>

                <ScrollReveal variant="fadeUp" delay={240}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                    {issues.map((issue) => (
                      <div key={issue} className="flex items-center gap-2.5">
                        <span className="w-4 h-4 flex items-center justify-center shrink-0">
                          <i className="ri-check-line text-sm" style={{ color: "#3C6FF8" }}></i>
                        </span>
                        <span className="font-manrope text-[14px] text-[#2B2B2B]/80">{issue}</span>
                      </div>
                    ))}
                  </div>
                </ScrollReveal>

                <ScrollReveal variant="fadeUp" delay={320}>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <a
                      href="/therapists"
                      className="inline-flex items-center gap-2 text-white font-manrope font-semibold text-[15px] px-7 py-4 rounded-full transition-colors cursor-pointer whitespace-nowrap"
                      style={{ background: "#3C6FF8" }}
                      onMouseEnter={(e) => (e.currentTarget as HTMLAnchorElement).style.background = "#2a5de6"}
                      onMouseLeave={(e) => (e.currentTarget as HTMLAnchorElement).style.background = "#3C6FF8"}
                    >
                      Find a Therapist <i className="ri-arrow-right-line"></i>
                    </a>
                  </div>
                </ScrollReveal>
              </div>
            </div>
          </div>
        </section>

        {/* What to expect */}
        <section className="w-full py-20 md:py-28" style={{ background: "linear-gradient(180deg, #F8F5EE 0%, #F5F1E8 45%, #F2EDE4 100%)" }}>
          <div className="w-full px-6 md:px-12 lg:px-20">
            <div className="max-w-3xl mb-14">
              <ScrollReveal variant="fadeIn" delay={0}>
                <span className="font-manrope text-[13px] font-semibold tracking-widest uppercase block mb-4" style={{ color: "#6B742F" }}>
                  What to Expect
                </span>
              </ScrollReveal>
              <ScrollReveal variant="fadeUp" delay={80}>
                <h2 className="font-fraunces text-[#2B2B2B] text-3xl md:text-4xl font-light leading-snug mb-5">
                  Understand what's beneath your patterns,{" "}
                  <em className="not-italic" style={{ color: "#7D54FC" }}>and build the insight to move forward</em>
                </h2>
              </ScrollReveal>
              <ScrollReveal variant="fadeUp" delay={160}>
                <p className="font-manrope text-[#2B2B2B]/78 text-[15px] leading-relaxed">
                  Our approach is structured, collaborative, and built around your specific needs. We don't do generic check-ins. Every session has a purpose.
                </p>
              </ScrollReveal>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
              {whatToExpect.map((item, i) => (
                <ScrollReveal key={item.num} variant="fadeUp" delay={i * 90}>
                  <div className="rounded-2xl p-7 border bg-white/80" style={{ borderColor: item.color + "22" }}>
                    <div className="h-[3px] w-10 rounded-full mb-5" style={{ background: item.color }} />
                    <div className="flex items-start gap-4">
                      <span className="font-manrope text-sm font-bold shrink-0 mt-0.5" style={{ color: item.color }}>{item.num}</span>
                      <div>
                        <h3 className="font-fraunces text-[#2B2B2B] text-lg font-medium mb-2">{item.title}</h3>
                        <p className="font-manrope text-[#2B2B2B]/78 text-[15px] leading-relaxed">{item.body}</p>
                      </div>
                    </div>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="w-full py-20 md:py-28">
          <div className="w-full px-6 md:px-12 lg:px-20">
            <div className="max-w-3xl mx-auto text-center mb-14">
              <ScrollReveal variant="fadeIn" delay={0}>
                <span className="font-manrope text-[13px] font-semibold tracking-widest uppercase block mb-4" style={{ color: "#6B742F" }}>
                  Getting Started
                </span>
              </ScrollReveal>
              <ScrollReveal variant="fadeUp" delay={80}>
                <h2 className="font-fraunces text-[#2B2B2B] text-3xl md:text-4xl font-light leading-snug mb-5">
                  Three steps to your first session
                </h2>
              </ScrollReveal>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12 relative">
              <div className="hidden md:block absolute top-8 left-[33%] right-[33%] h-px" style={{ background: "linear-gradient(90deg, #B4A6E3 0%, #3C6FF8 100%)", opacity: 0.35 }}></div>
              {[
                {
                  num: "01",
                  icon: "ri-user-search-line",
                  title: "Choose your therapist",
                  body: "Browse profiles and find someone whose specialty and style match what you need. Or reach out for a recommendation.",
                  color: "#7D54FC",
                  bg: "rgba(125,84,252,0.1)",
                  border: "rgba(125,84,252,0.2)",
                },
                {
                  num: "02",
                  icon: "ri-calendar-2-line",
                  title: "Book your session",
                  body: "Schedule online at a time that works for you. In-person or telehealth, weekday or evening. Whatever fits your life.",
                  color: "#3C6FF8",
                  bg: "rgba(60,111,248,0.1)",
                  border: "rgba(60,111,248,0.2)",
                },
                {
                  num: "03",
                  icon: "ri-seedling-line",
                  title: "Begin with direction",
                  body: "Your first session is focused and purposeful. You'll leave with clarity on what you're working toward and a plan to get there.",
                  color: "#6B742F",
                  bg: "rgba(107,116,47,0.1)",
                  border: "rgba(107,116,47,0.2)",
                },
              ].map((s, i) => (
                <ScrollReveal key={s.num} variant="fadeUp" delay={i * 120}>
                  <div className="flex flex-col items-center text-center">
                    <div className="w-14 h-14 flex items-center justify-center rounded-full border mb-5" style={{ background: "rgba(255,255,255,0.8)", borderColor: s.border }}>
                      <i className={`${s.icon} text-xl`} style={{ color: s.color }}></i>
                    </div>
                    <span className="font-manrope text-[12px] font-semibold tracking-widest uppercase mb-3" style={{ color: s.color }}>
                      Step {s.num}
                    </span>
                    <h3 className="font-fraunces text-[#2B2B2B] text-xl font-medium leading-snug mb-3">{s.title}</h3>
                    <p className="font-manrope text-[#2B2B2B]/78 text-[15px] leading-relaxed">{s.body}</p>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="w-full py-20 md:py-28" style={{ background: "linear-gradient(180deg, #F2EEE4 0%, #EEE8DA 45%, #F5F1E8 100%)" }}>
          <div className="w-full px-6 md:px-12 lg:px-20">
            <div className="max-w-2xl mx-auto text-center">
              <ScrollReveal variant="fadeUp" delay={0}>
                <h2 className="font-fraunces text-[#2B2B2B] text-3xl md:text-4xl font-light leading-snug mb-5">
                  Individual sessions start at $125
                </h2>
                <p className="font-manrope text-[#2B2B2B]/78 text-[15px] leading-relaxed mb-8 max-w-lg mx-auto">
                  Rates vary by therapist and are listed on each profile. No hidden fees, no required packages. A free 15-minute consultation is available before you commit.
                </p>
              </ScrollReveal>
              <ScrollReveal variant="fadeUp" delay={80}>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a
                    href="/therapists"
                    className="inline-flex items-center justify-center gap-2 text-white font-manrope font-semibold text-[15px] px-7 py-4 rounded-full transition-colors cursor-pointer whitespace-nowrap"
                    style={{ background: "#3C6FF8" }}
                    onMouseEnter={(e) => (e.currentTarget as HTMLAnchorElement).style.background = "#2a5de6"}
                    onMouseLeave={(e) => (e.currentTarget as HTMLAnchorElement).style.background = "#3C6FF8"}
                  >
                    Find Your Therapist <i className="ri-arrow-right-line"></i>
                  </a>
                </div>
              </ScrollReveal>
            </div>
          </div>
        </section>
      </PageLayout>
    </>
  );
}